import * as React from 'react';
import { isDefined, noop } from 'react-financial-charts';
import { getXValue } from '@react-financial-charts/core/lib/utils/ChartDataUtil';
import { saveNodeType } from 'react-financial-charts';
import { HoverTextNearMouse } from '@react-financial-charts/interactive/lib/components/index';
import { fansWithColor, levelsPropetyWithColor } from '@/utils/defaultColors';
import type { Coordinate } from '@/types/properties';
import type { Levels } from '@/types/commontypes';
import type { DrawingSharedState, HoverTextConfig, MoreProps } from '@/types/commontypes';
import { GannBoxShape, GannFanShape, ClickableCircle } from '.';
import { fixedAppearance } from '@/utils/defaultShapePropertyValues';

export interface GannFanDragData {
  startXY: [number, number];
  endXY: [number, number];
  dy?: number;
}

export interface EachGannFanProps {
  readonly startXY: Coordinate;
  readonly endXY: Coordinate;
  readonly dy?: number;
  readonly interactive: boolean;
  readonly selected: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  readonly appearance: Record<string, any>;
  readonly hoverText: HoverTextConfig;
  readonly index?: number;
  readonly uuid: string;
  readonly onDrag: (e: React.MouseEvent, uuid: string, moreProps: GannFanDragData) => void;
  readonly onDragComplete?: (e: React.MouseEvent, moreProps: MoreProps) => void;
  readonly isGannBox?: boolean;

  readonly priceLevels?: Levels[];
  readonly timeLevels?: Levels[];

  readonly onSelect?: (e: React.MouseEvent, uuid: string, moreProps: MoreProps) => void;
  readonly onDragStart?: (uuid: string) => void;
  readonly onHover?: (uuid: string | null, moreProps?: MoreProps) => void;
  readonly locked: boolean;
  readonly onDoubleClick?: (e: React.MouseEvent, moreProps: MoreProps) => void;
  readonly sharedState?: DrawingSharedState;
  readonly isAnyShapeEnabled?: boolean;
}

interface EachGannFanState {
  hover: boolean;
  dragging: boolean;
}

export class EachGannFan extends React.Component<EachGannFanProps, EachGannFanState> {
  public static defaultProps = {
    yDisplayFormat: (d: number) => d.toFixed(2),
    interactive: true,
    selected: false,
    appearance: {
      stroke: '#000000',
      strokeWidth: 1,
      ...fixedAppearance,
      fontFamily: "-apple-system, system-ui, Roboto, 'Helvetica Neue', Ubuntu, sans-serif",
      fontSize: 10,
      fontFill: '#000000',
    },
    onDrag: noop,
    hoverText: {
      ...HoverTextNearMouse.defaultProps,
      enable: true,
      bgHeight: 18,
      bgWidth: 120,
      text: 'Click to select object',
    },
    isGannBox: false,
    priceLevels: levelsPropetyWithColor,
    timeLevels: levelsPropetyWithColor,
    locked: false,
  };

  private lineInteractive: string;
  private dragStart!: { startXY: Coordinate; endXY: Coordinate; dy?: number };
  private saveNodeType: <T>(type: string) => React.Ref<T>;

  public constructor(props: EachGannFanProps) {
    super(props);

    this.saveNodeType = saveNodeType.bind(this);
    this.lineInteractive = 'react-financial-charts-pointer-cursor';
    this.state = {
      hover: false,
      dragging: false,
    };
  }

  public render() {
    const { isGannBox, timeLevels = [], priceLevels = [] } = this.props;
    const { startXY, endXY } = this.props;
    const { interactive, appearance } = this.props;
    const { hoverText, selected, onDoubleClick } = this.props;
    const {
      edgeFill,
      stroke,
      strokeWidth,
      fontFamily,
      fontSize,
      fontFill,
      showFansWithColor,
      timeBgOpacity,
      priceBgOpacity,
      backgroundOpacity,
      r: _r,
    } = appearance;
    const { hover } = this.state;
    const { enable: hoverTextEnabled, ...restHoverTextProps } = hoverText;

    const { dragging } = this.state;
    if (dragging) {
      this.lineInteractive = 'react-financial-charts-grabbing-cursor';
    } else if (!dragging) {
      this.lineInteractive = 'react-financial-charts-pointer-cursor';
    } else {
      this.lineInteractive = 'react-financial-charts-pointer-cursor';
    }

    const isValidXY = (xy: unknown) =>
      Array.isArray(xy) && xy.length >= 2 && typeof xy[0] === 'number' && typeof xy[1] === 'number';

    if (!isValidXY(startXY) || !isValidXY(endXY)) {
      return null;
    }
    const hoverHandler: {
      onHover?: (e: React.MouseEvent, moreProps: MoreProps) => void;
      onUnHover?: (e: React.MouseEvent, moreProps: MoreProps) => void;
    } = interactive ? { onHover: this.handleHover, onUnHover: this.handleHover } : {};
    const line1Edge =
      isDefined(startXY) && isDefined(endXY) ? (
        <g>
          {this.getEdgeCircle({
            xy: startXY,
            dragHandler: this.handleLine1Edge1Drag,
            cursor: 'big-declined-arrow-cursor',
            fill: edgeFill,
            edge: 'edge1',
          })}
          {this.getEdgeCircle({
            xy: endXY,
            dragHandler: this.handleLine1Edge2Drag,
            cursor: 'big-declined-arrow-cursor',
            fill: edgeFill,
            edge: 'edge2',
          })}

          {isGannBox && (
            <>
              {this.getEdgeCircle({
                xy: [endXY[0], startXY[1]],
                dragHandler: this.handleLine2Edge1Drag,
                cursor: 'big-declined-arrow-cursor',
                fill: edgeFill,
                edge: 'edge1',
              })}
              {this.getEdgeCircle({
                xy: [startXY[0], endXY[1]],
                dragHandler: this.handleLine2Edge2Drag,
                cursor: 'big-declined-arrow-cursor',
                fill: edgeFill,
                edge: 'edge2',
              })}
            </>
          )}
        </g>
      ) : null;

    const GannShape =
      isDefined(isGannBox) && isGannBox === true ? (
        <g>
          <GannBoxShape
            ref={this.saveNodeType('fan')}
            selected={hover || selected}
            {...hoverHandler}
            startXY={startXY}
            endXY={endXY}
            strokeStyle={stroke}
            strokeWidth={strokeWidth}
            fontFamily={fontFamily}
            fontSize={fontSize}
            fontFill={fontFill}
            timeLevels={timeLevels}
            priceLevels={priceLevels}
            timeBgOpacity={timeBgOpacity}
            priceBgOpacity={priceBgOpacity}
            interactiveCursorClass={this.lineInteractive}
            onDragStart={this.handleDragStart}
            onDrag={this.handleFanDrag}
            onDragComplete={this.handleDragComplete}
            onMouseDown={this.handleShapeMouseDown}
            onDoubleClick={onDoubleClick}
          />
        </g>
      ) : (
        <g>
          <GannFanShape
            ref={this.saveNodeType('fan')}
            selected={hover || selected}
            {...hoverHandler}
            startXY={startXY}
            endXY={endXY}
            strokeStyle={stroke}
            strokeWidth={strokeWidth}
            colorWithShow={showFansWithColor || fansWithColor}
            fontFamily={fontFamily}
            fontSize={fontSize}
            fontFill={fontFill}
            backgroundOpacity={backgroundOpacity}
            interactiveCursorClass={this.lineInteractive}
            onDragStart={this.handleDragStart}
            onDrag={this.handleFanDrag}
            onDragComplete={this.handleDragComplete}
            onMouseDown={this.handleShapeMouseDown}
            onDoubleClick={onDoubleClick}
          />
        </g>
      );

    return (
      <g>
        {GannShape}
        {line1Edge}
        <HoverTextNearMouse show={hoverTextEnabled && hover && !selected} {...restHoverTextProps} />
      </g>
    );
  }

  private readonly handleDragComplete = (e: React.MouseEvent, moreProps: MoreProps) => {
    this.setState({
      dragging: false,
    });

    const { onDragComplete, locked } = this.props;
    if (onDragComplete === undefined || locked || moreProps.hovering === false) {
      return;
    }

    onDragComplete(e, moreProps);
  };

  private readonly handleShapeMouseDown = (e: React.MouseEvent, moreProps: MoreProps) => {
    const { onSelect, uuid, sharedState } = this.props;
    if (sharedState?.status === 'started') return;
    if (onSelect && isDefined(uuid)) {
      onSelect(e, uuid, moreProps);
    }
  };

  private readonly getEdgeCircle = ({
    xy,
    dragHandler,
    cursor,
    fill,
    edge,
  }: {
    xy: Coordinate;
    dragHandler: (e: React.MouseEvent, moreProps: MoreProps) => void;
    cursor: string;
    fill: string;
    edge: string;
  }) => {
    const { hover } = this.state;
    const { selected, appearance, onDoubleClick } = this.props;
    const { edgeStroke, edgeStrokeWidth, r } = appearance;
    const { onDragComplete, locked } = this.props;
    const lockedR = locked ? r - 2 : r;
    const lockedEdgeStorke = locked ? '#436fe9' : edgeStroke;
    const lockedStrokeWidth = locked ? 1 : edgeStrokeWidth;
    return (
      <ClickableCircle
        ref={this.saveNodeType(edge)}
        show={selected || hover || locked}
        cx={xy[0]}
        cy={xy[1]}
        r={lockedR}
        fillStyle={fill}
        strokeStyle={lockedEdgeStorke}
        strokeWidth={lockedStrokeWidth}
        interactiveCursorClass={cursor}
        onDragStart={this.handleDragStart}
        onDrag={dragHandler}
        onDragComplete={onDragComplete}
        onDoubleClick={onDoubleClick}
      />
    );
  };

  private readonly handleLine1Edge2Drag = (e: React.MouseEvent, moreProps: MoreProps) => {
    const { uuid, onDrag, onDragStart, locked } = this.props;
    if (locked) return;
    const { endXY } = this.dragStart;

    if (onDragStart && uuid !== undefined) {
      onDragStart(uuid);
    }
    const {
      mouseXY,
      xAccessor,
      xScale,
      fullData,
      chartConfig: { yScale },
    } = moreProps;
    const startPos = moreProps.startPos!;
    const hovering = moreProps.hovering || false;

    const dx = startPos[0] - mouseXY[0];
    const dy = startPos[1] - mouseXY[1];

    const x1 = xScale(endXY[0]);
    const y1 = yScale(endXY[1]);

    const newX1Value = getXValue(xScale, xAccessor, [x1 - dx, y1 - dy], fullData);
    const newY1Value = yScale.invert(y1 - dy);

    if (hovering) {
      onDrag(e, uuid, {
        startXY: this.dragStart.startXY,
        endXY: [newX1Value, newY1Value],
        dy: this.dragStart.dy,
      });
    }
  };

  private readonly handleLine1Edge1Drag = (e: React.MouseEvent, moreProps: MoreProps) => {
    const { uuid, onDrag, onDragStart, locked } = this.props;
    if (locked) return;
    const { startXY } = this.dragStart;

    if (onDragStart && uuid !== undefined) {
      onDragStart(uuid);
    }
    const {
      mouseXY,
      xAccessor,
      xScale,
      fullData,
      chartConfig: { yScale },
    } = moreProps;
    const startPos = moreProps.startPos!;
    const hovering = moreProps.hovering || false;
    const dx = startPos[0] - mouseXY[0];
    const dy = startPos[1] - mouseXY[1];

    const x1 = xScale(startXY[0]);
    const y1 = yScale(startXY[1]);

    const newX1Value = getXValue(xScale, xAccessor, [x1 - dx, y1 - dy], fullData);
    const newY1Value = yScale.invert(y1 - dy);
    if (hovering) {
      onDrag(e, uuid, {
        startXY: [newX1Value, newY1Value],
        endXY: this.dragStart.endXY,
        dy: this.dragStart.dy,
      });
    }
  };

  private readonly handleLine2Edge1Drag = (e: React.MouseEvent, moreProps: MoreProps) => {
    const { uuid, onDrag, onDragStart, locked } = this.props;
    if (locked) return;
    const { startXY, endXY } = this.dragStart;

    if (onDragStart && uuid !== undefined) {
      onDragStart(uuid);
    }
    const {
      mouseXY,
      xAccessor,
      xScale,
      fullData,
      chartConfig: { yScale },
    } = moreProps;
    const startPos = moreProps.startPos!;
    const hovering = moreProps.hovering || false;

    const dx = startPos[0] - mouseXY[0];
    const dy = startPos[1] - mouseXY[1];

    const x1 = xScale(endXY[0]);
    const y1 = yScale(startXY[1]);

    const newX1Value = getXValue(xScale, xAccessor, [x1 - dx, y1 - dy], fullData);
    const newY1Value = yScale.invert(y1 - dy);
    if (hovering) {
      onDrag(e, uuid, {
        startXY: [startXY[0], newY1Value],
        endXY: [newX1Value, endXY[1]],
        dy: this.dragStart.dy,
      });
    }
  };

  private readonly handleLine2Edge2Drag = (e: React.MouseEvent, moreProps: MoreProps) => {
    const { uuid, onDrag, onDragStart, locked } = this.props;
    if (locked) return;
    const { startXY, endXY } = this.dragStart;

    if (onDragStart && uuid !== undefined) {
      onDragStart(uuid);
    }
    const {
      mouseXY,
      xAccessor,
      xScale,
      fullData,
      chartConfig: { yScale },
    } = moreProps;
    const startPos = moreProps.startPos!;
    const hovering = moreProps.hovering || false;

    const dx = startPos[0] - mouseXY[0];
    const dy = startPos[1] - mouseXY[1];

    const x1 = xScale(startXY[0]);
    const y1 = yScale(endXY[1]);

    const newX1Value = getXValue(xScale, xAccessor, [x1 - dx, y1 - dy], fullData);
    const newY1Value = yScale.invert(y1 - dy);

    if (hovering) {
      onDrag(e, uuid, {
        startXY: [newX1Value, startXY[1]],
        endXY: [endXY[0], newY1Value],
        dy: this.dragStart.dy,
      });
    }
  };

  private readonly handleFanDrag = (e: React.MouseEvent, moreProps: MoreProps) => {
    const { uuid, onDrag, locked, onDragStart } = this.props;
    if (locked) return;
    const { startXY, endXY } = this.dragStart;
    if (onDragStart && isDefined(uuid)) {
      onDragStart(uuid);
    }
    const {
      xScale,
      chartConfig: { yScale },
      xAccessor,
      fullData,
      mouseXY,
    } = moreProps;
    const startPos = moreProps.startPos!;
    const hovering = moreProps.hovering || false;

    const x1 = xScale(startXY[0]);
    const y1 = yScale(startXY[1]);
    const x2 = xScale(endXY[0]);
    const y2 = yScale(endXY[1]);

    const dx = startPos[0] - mouseXY[0];
    const dy = startPos[1] - mouseXY[1];

    const newX1Value = getXValue(xScale, xAccessor, [x1 - dx, y1 - dy], fullData);
    const newY1Value = yScale.invert(y1 - dy);
    const newX2Value = getXValue(xScale, xAccessor, [x2 - dx, y2 - dy], fullData);
    const newY2Value = yScale.invert(y2 - dy);

    this.setState({ dragging: true });
    // const newDy = newY2Value - endXY[1] + this.dragStart.dy;
    if (hovering) {
      onDrag(e, uuid, {
        startXY: [newX1Value, newY1Value],
        endXY: [newX2Value, newY2Value],
        dy: this.dragStart.dy,
      });
    }
  };

  private readonly handleDragStart = (_: React.MouseEvent, moreProps: MoreProps) => {
    const { startXY, endXY, dy, onDragStart, uuid, locked } = this.props;
    if (locked) return;
    if (onDragStart && uuid !== undefined && (moreProps.hovering || false)) {
      onDragStart(uuid);
    }
    this.setState({ dragging: true });
    this.dragStart = {
      startXY,
      endXY,
      dy,
    };
  };

  private readonly handleHover = (_: React.MouseEvent, moreProps: MoreProps) => {
    const { uuid, onHover, sharedState, isAnyShapeEnabled } = this.props;
    if (sharedState?.status === 'started' || isAnyShapeEnabled) return;
    const hovering = moreProps.hovering || false;
    if (this.state.hover !== hovering) {
      this.setState({
        hover: hovering,
      });
      if (hovering && onHover) {
        onHover(uuid ?? null, moreProps);
      }
    }
  };
}
